import { Link, Text } from "@ttc3k/trekker";
import { EnumOrderDeliveryMethod, OrderByIdQuery } from "gql/generated";
import { DeliveryTruck, Shop } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { ReservationSection } from "./ReservationSection";

type Order = NonNullable<OrderByIdQuery["orderById"]>;

export const formatAddressLines = (
  address: NonNullable<Order["delivery"]>["shippingAddress"],
) =>
  [
    address?.line1,
    address?.line2,
    [address?.city, address?.subdivision, address?.postalCode]
      .filter(Boolean)
      .join(" "),
    address?.country,
  ]
    .map(part => part?.trim())
    .filter(Boolean);

const TRACKING_URLS: [RegExp, (trackingNumber: string) => string][] = [
  [
    /canada\s*post|postes/i,
    n =>
      `https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${n}`,
  ],
  [/purolator/i, n => `https://www.purolator.com/en/shipping/tracker?pin=${n}`],
  [/\bups\b/i, n => `https://www.ups.com/track?tracknum=${n}`],
  [/fedex/i, n => `https://www.fedex.com/fedextrack/?trknbr=${n}`],
  [/usps/i, n => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`],
  [/dhl/i, n => `https://www.dhl.com/en/express/tracking.html?AWB=${n}`],
];

/** Carrier tracking page for well-known carriers; null when we can't tell. */
export const getTrackingUrl = (
  carrier: string | null | undefined,
  trackingNumber: string | null | undefined,
): string | null => {
  const number = trackingNumber?.trim();
  if (!carrier || !number) return null;
  const match = TRACKING_URLS.find(([pattern]) => pattern.test(carrier));
  return match ? match[1](encodeURIComponent(number)) : null;
};

const DetailBlock = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex flexDir="column" gap="25" w="full" p="250">
    <Text visual="smallSemiBold" color="text.light">
      {label}
    </Text>
    {children}
  </Flex>
);

/** Merch orders: where the order is going (or where to collect it), plus tracking once shipped. */
export const DeliveryDetails = ({ order }: { order: Order }) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.DELIVERY",
  });
  const delivery = order.delivery;
  if (!delivery) return null;

  const isShip = delivery.method === EnumOrderDeliveryMethod.Ship;
  const carrier = order.fulfillment?.carrier?.trim();
  const trackingNumber = order.fulfillment?.trackingNumber?.trim();
  const trackingUrl = getTrackingUrl(carrier, trackingNumber);
  const lines = isShip
    ? formatAddressLines(delivery.shippingAddress)
    : [delivery.pickupLocationName, delivery.pickupLocationAddress]
        .map(part => part?.trim())
        .filter(Boolean);

  return (
    <ReservationSection
      icon={isShip ? DeliveryTruck : Shop}
      title={isShip ? t("TITLE_SHIP") : t("TITLE_PICKUP")}
    >
      <Flex
        flexDir="column"
        w="full"
        bgColor="ink.100"
        border="2px solid"
        borderColor="border.light"
        borderRadius="100"
      >
        <DetailBlock label={isShip ? t("SHIP_TO") : t("PICKUP_AT")}>
          {lines.map(line => (
            <Text key={line} visual="bodyMedium" color="text.mid">
              {line}
            </Text>
          ))}
        </DetailBlock>
        {isShip && (carrier || trackingNumber) && (
          <DetailBlock label={t("TRACKING")}>
            <Text visual="bodyMedium" color="text.mid">
              {[carrier, trackingNumber].filter(Boolean).join(" · ")}
            </Text>
            {trackingUrl && (
              <Link
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("TRACK_PACKAGE")}
              </Link>
            )}
          </DetailBlock>
        )}
      </Flex>
    </ReservationSection>
  );
};
